import { SortTypes } from './types.domain';

export const OVERDRAFT_QUARTERLY_REVIEW_MAX_ATTEMPTS = 3;

export type OverdraftQuarterlyReviewRunStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

export type OverdraftQuarterlyReviewAccountStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'skipped'
  | 'failed';

export type OverdraftQuarterlyReviewStep =
  | 'account_details'
  | 'statements'
  | 'dishonours'
  | 'classification'
  | 'opportunity'
  | 'bank_statement_analysis'
  | 'customer_notification'
  | 'completed';

export type OverdraftQuarterlyReviewEmailStatus =
  | 'not_required'
  | 'pending'
  | 'sending'
  | 'sent'
  | 'failed'
  | 'ambiguous';

export type OverdraftQuarterlyReviewBankStatementStatus =
  | 'not_checked'
  | 'valid'
  | 'missing'
  | 'stale'
  | 'error';

export interface OverdraftQuarterlyReviewRun {
  id: number;
  reviewYear: number;
  reviewQuarter: number;
  periodStart: Date;
  periodEnd: Date;
  asOfAt: Date;
  status: OverdraftQuarterlyReviewRunStatus;
  revision: number;
  lenderListJson: string;
  excludedLenderListJson: string;
  summaryEmailStatus: OverdraftQuarterlyReviewEmailStatus;
  summaryEmailRevision: number | null;
  summaryEmailStartedAt: Date | null;
  summaryEmailSentAt: Date | null;
  lastError: string | null;
  lastRetriedByUserId: number | null;
  lastRetriedAt: Date | null;
  lastManualRetryReason: string | null;
  createdAt: Date;
  startedAt: Date | null;
  completedAt: Date | null;
  lastUpdate: Date;
}

export interface OverdraftQuarterlyReviewAccount {
  id: number;
  runId: number;
  pismoAccountId: number;
  opportunityId: string | null;
  salesforceAccountId: string | null;
  status: OverdraftQuarterlyReviewAccountStatus;
  currentStep: OverdraftQuarterlyReviewStep;
  stepAttemptCount: number;
  maxAttempts: number;
  processingRevision: number;
  processingStartedAt: Date | null;
  lastError: string | null;
  pismoAccountStatus: string | null;
  isAccountCancelled: boolean | null;
  accountName: string | null;
  currentBalance: number | null;
  accountLimit: number | null;
  triggerFacilityUtilisation: boolean;
  triggerDishonour: boolean;
  triggerArrears: boolean;
  isTriggered: boolean;
  abn: string | null;
  bankStatementStatus: OverdraftQuarterlyReviewBankStatementStatus;
  bankStatementEndDate: Date | null;
  revenue0: number | null;
  revenue1: number | null;
  revenue2: number | null;
  revenue3: number | null;
  previousAvgRevenues: number | null;
  newLoanPaymentPartiesJson: string | null;
  dishonourTransactionsJson: string | null;
  customerEmailStatus: OverdraftQuarterlyReviewEmailStatus;
  customerEmailSentAt: Date | null;
  createdAt: Date;
  completedAt: Date | null;
  lastUpdate: Date;
}

export interface OverdraftQuarterlyReviewStatus {
  run: OverdraftQuarterlyReviewRun;
  totalAccounts: number;
  pendingAccounts: number;
  processingAccounts: number;
  completedAccounts: number;
  skippedAccounts: number;
  failedAccounts: number;
}

export interface RetryOverdraftQuarterlyReviewBody {
  year: number;
  quarter: number;
  reason: string;
}

export interface RetryOverdraftQuarterlyReviewResult extends OverdraftQuarterlyReviewStatus {
  preservedAccounts: number;
  resetAccounts: number;
}

export type OverdraftQuarterlyReviewAccountSortType = SortTypes<
  | 'pismoAccountId'
  | 'accountName'
  | 'status'
  | 'currentStep'
  | 'bankStatementStatus'
  | 'stepAttemptCount'
  | 'lastUpdate'
>;

export interface OverdraftQuarterlyReviewAccountFilters {
  status?: OverdraftQuarterlyReviewAccountStatus;
  pismoAccountId?: number;
}

export interface OverdraftQuarterlyReviewRunListResult {
  totalRows: number;
  records: OverdraftQuarterlyReviewStatus[];
}

export interface OverdraftQuarterlyReviewAccountListResult {
  totalRows: number;
  records: OverdraftQuarterlyReviewAccount[];
}

export interface RetryOverdraftQuarterlyReviewAccountBody extends RetryOverdraftQuarterlyReviewBody {
  pismoAccountId: number;
}

export interface RetryOverdraftQuarterlyReviewAccountResult extends OverdraftQuarterlyReviewStatus {
  account: OverdraftQuarterlyReviewAccount;
}
